import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useCitiesListContext } from '@/features/marketplace/hooks/use-get-cities';
import { useLocale } from '@/hooks/use-locale';
import { COLOC_MAIN_APP } from '@/config';
import { CityDetailResults } from '@/types/marketplace';
import { CitiesGrid } from '../cities-grid';

interface CitiesGridModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCitySelect: (city: CityDetailResults) => void;
  position?: 'centered' | 'below-header';
  dropdownRef?: React.RefObject<HTMLDivElement>;
  onAllCitiesClick?: () => void;
}

const CitiesGridModal: React.FC<CitiesGridModalProps> = ({
  isOpen,
  onClose,
  onCitySelect,
  position = 'below-header',
  dropdownRef,
  onAllCitiesClick,
}) => {
  const { citiesList } = useCitiesListContext();
  const { t } = useTranslation();
  const { currentLocale } = useLocale();

  const handleCityClick = (city: CityDetailResults) => {
    onCitySelect(city);
    onClose();
  };

  const handleAllCitiesClick = () => {
    window.location.href = `${COLOC_MAIN_APP}/${currentLocale}/shared-houses`;
  };

  if (typeof document === 'undefined') return null;

  if (!isOpen) {
    return null;
  }

  const modalContent = (
    <div
      ref={dropdownRef}
      data-cities-dropdown="true"
      style={{
        transform: position === 'centered' ? 'translate(-50%, -50%)' : 'translateX(-50%)',
        backgroundColor: 'rgba(255, 255, 255, 0.80)',
        backdropFilter: 'blur(12px)',
      }}
      className={clsx(
        'fixed left-[50%] z-[60] overflow-y-auto',
        'w-[calc(100%-theme(space.7))] max-w-[130rem]',
        'rounded-[24px] p-5',
        'sm:w-[80%]',
        position === 'centered' ? 'top-1/2' : 'top-[11rem]'
      )}
    >
      <CitiesGrid
        cities={citiesList}
        onCityClick={handleCityClick}
        title={t('js.components.all_cities_dropdown.discover_new_home')}
        showAllCities
        onAllCitiesClick={onAllCitiesClick ?? handleAllCitiesClick}
      />
    </div>
  );

  return createPortal(modalContent, document.body);
};

export { CitiesGridModal };
